import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, Users, LogOut, User, Package } from 'lucide-react';
import { useApp } from '../App';
import CartDrawer from './CartDrawer';

const Header = () => {
  const { selectedRaffle, cart, user, logout, adminUser } = useApp();
  const [cartOpen, setCartOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const percentage = selectedRaffle?.percentage || 0;
  const sold = selectedRaffle?.sold || 0;
  const total = selectedRaffle?.total_chests || 5000;

  return (
    <>
      <header className="sticky top-0 z-40 backdrop-blur supports-[backdrop-filter]:bg-[rgba(9,10,13,0.72)] bg-[rgba(9,10,13,0.88)] border-b border-white/[0.08]" data-testid="main-header">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <span className="text-2xl font-heading font-bold text-[#FFC640] tracking-tight">COFRES</span>
              <span className="text-2xl font-accent font-semibold text-[#F7F2E7]">MÁGICOS</span>
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/" className={`text-sm font-medium transition-colors hover:text-[#FFC640] ${location.pathname === '/' ? 'text-[#FFC640]' : 'text-[#F7F2E7]/80'}`}>Inicio</Link>
              {user && (
                <Link to="/cofres" className={`text-sm font-medium transition-colors hover:text-[#FFC640] flex items-center gap-1.5 ${location.pathname === '/cofres' ? 'text-[#FFC640]' : 'text-[#F7F2E7]/80'}`}>
                  <Package size={16} /> Cofres
                </Link>
              )}
              <Link to="/mi-red" className={`text-sm font-medium transition-colors hover:text-[#FFC640] flex items-center gap-1.5 ${location.pathname === '/mi-red' ? 'text-[#FFC640]' : 'text-[#F7F2E7]/80'}`}>
                <Users size={16} /> Mi Red
              </Link>
            </nav>
            <div className="flex items-center gap-2">
              {user && (
                <div className="hidden md:flex items-center gap-2">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#121521] border border-white/[0.08]">
                    <User size={14} className="text-[#FFC640]" />
                    <span className="text-xs text-[#F7F2E7]/70 max-w-[100px] truncate">{user.name?.split(' ')[0]}</span>
                  </div>
                  <button onClick={logout} className="p-2 rounded-xl bg-[#121521] border border-white/[0.08] hover:border-red-500/30" title="Cerrar sesión">
                    <LogOut size={16} className="text-[#F7F2E7]/50" />
                  </button>
                </div>
              )}
              {user && (
                <button onClick={() => setCartOpen(true)} className="relative p-2.5 rounded-xl bg-[#121521] border border-white/[0.08] hover:border-[rgba(255,198,64,0.35)] transition-colors" data-testid="cart-open-button" aria-label="Carrito">
                  <ShoppingCart size={20} className="text-[#FFC640]" />
                  {cart.count > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FFC640] text-[#090A0D] text-xs font-bold rounded-full flex items-center justify-center">{cart.count}</span>}
                </button>
              )}
              <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden p-2 rounded-xl bg-[#121521] border border-white/[0.08]" aria-label="Menú">
                {menuOpen ? <X size={20} className="text-[#F7F2E7]" /> : <Menu size={20} className="text-[#F7F2E7]" />}
              </button>
            </div>
          </div>
          {/* Progress bar */}
          {selectedRaffle && (
            <div className="pb-3">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-[#F7F2E7]/70">
                  {selectedRaffle.name && <span className="text-[#FFC640] mr-2">{selectedRaffle.name}</span>}
                  Cofres vendidos: <span className="text-[#FFC640] font-semibold">{sold.toLocaleString()}</span> / {total.toLocaleString()}
                </span>
                <span className="text-[#FFC640] font-heading font-semibold tabular-nums">{percentage}%</span>
              </div>
              <div className="w-full h-2 bg-[#121521] rounded-full overflow-hidden border border-white/[0.06]">
                <div className="h-full bg-gradient-to-r from-[#D4AF37] to-[#FFC640] rounded-full transition-all duration-1000 ease-out" style={{ width: `${percentage}%` }} />
              </div>
              <div className="text-xs text-[#F7F2E7]/50 mt-1">Restantes: {(total - sold).toLocaleString()}</div>
            </div>
          )}
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-white/[0.08] bg-[#0E1016] px-4 py-3">
            <Link to="/" className="block py-2 text-sm text-[#F7F2E7]/80 hover:text-[#FFC640]" onClick={() => setMenuOpen(false)}>Inicio</Link>
            {user && <Link to="/cofres" className="block py-2 text-sm text-[#F7F2E7]/80 hover:text-[#FFC640] flex items-center gap-2" onClick={() => setMenuOpen(false)}><Package size={16} /> Cofres</Link>}
            <Link to="/mi-red" className="block py-2 text-sm text-[#F7F2E7]/80 hover:text-[#FFC640] flex items-center gap-2" onClick={() => setMenuOpen(false)}><Users size={16} /> Mi Red</Link>
            {user && (
              <>
                <div className="border-t border-white/[0.08] my-2" />
                <div className="py-1 text-xs text-[#F7F2E7]/40">{user.name}</div>
                <button onClick={() => { logout(); setMenuOpen(false); }} className="block py-2 text-sm text-red-400/70 hover:text-red-400 flex items-center gap-2"><LogOut size={14} /> Cerrar sesión</button>
              </>
            )}
          </div>
        )}
      </header>
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </>
  );
};

export default Header;
